import type { Certification } from '@/api/certifications/types';
import type {
  Badge,
  BomHeader,
  BomHeadersListParams,
  BomHeadersListResponse,
  BomLine,
  BomLinesListParams,
  BomLinesListResponse,
  BomPlacement,
  BomPlacementsListParams,
  BomPlacementsListResponse,
  CascadeMappingsListResponse,
  CheckStyleCodesBody,
  CheckStyleCodesItem,
  CompanyDropdownOption,
  CreateBomHeaderBody,
  CreateBomLineBody,
  CreateBomPlacementBody,
  CreateMaterialHeaderBody,
  CreateMaterialLineBody,
  CreateSkuBody,
  CreateStyleLinkBody,
  CreateVariantBody,
  MaterialHeader,
  MaterialHeadersListParams,
  MaterialHeadersListResponse,
  MaterialLine,
  MaterialLinesListParams,
  MaterialLinesListResponse,
  ProductBadgesResponse,
  SkuDetail,
  SkuListPage,
  SkusListParams,
  StyleDetail,
  StyleListPage,
  StylePropertyPage,
  StylesListParams,
  StyleTracingOrdersResponse,
  StyleTypeFieldValuesResponse,
  SupplyChainGraphResponse,
  SupplyChainMappingsListResponse,
  UpdateBomHeaderBody,
  UpdateBomLineBody,
  UpdateMaterialHeaderBody,
  UpdateMaterialLineBody,
  UpdateSkuBody,
  UpdateStyleBody,
  UpdateVariantBody,
  UpsertStyleTypeFieldValuesBody,
  VariantDetail,
  VariantListPage,
  VariantsListParams,
} from '@/api/my-styles/types';
import type { EntityKindPath } from '@/app/(client)/my-styles/-hooks/useEntityDetail';
import {
  API_BASE_URL,
  API_ROOT_URL,
  BACKEND_URL,
  CASCADE_MAP_SERVICE_URL,
} from '@/lib/constants/api';
import { apiFetch } from '@/lib/utils/apiFetch';

// ─── Certifications (legacy per-entity endpoint) ──────────────────────────────

export interface EntityCertificationsParams {
  page: number; // 1-based; converted to 0-based on the wire
  count: number;
  sort?: string;
  /** JSON-stringified record of `{ field: csvValue }`, per legacy contract. */
  filter?: string;
}

export interface EntityCertificationsResponse {
  data: Certification[];
  meta: {
    totalCount?: number;
    total?: number;
    page?: number;
    count?: number;
  };
}

/**
 * The legacy backend resolves `:id` against styles, variants, and SKUs alike,
 * which is why the old frontend reuses one component for all three detail
 * pages. We keep the same behaviour: the caller passes the URL `id` regardless
 * of `kind`.
 */
export function fetchEntityCertifications(
  entityId: string,
  params: EntityCertificationsParams,
): Promise<EntityCertificationsResponse> {
  // Legacy endpoint expects 1-indexed pages (`page=1` is the first page);
  // matches what the old frontend sent.
  const qs = new URLSearchParams({
    page: String(Math.max(1, params.page)),
    count: String(params.count),
  });
  if (params.sort) qs.set('sort', params.sort);
  if (params.filter) qs.set('filter', params.filter);
  return apiFetch<EntityCertificationsResponse>(
    `/styles/${entityId}/certifications?${qs.toString()}`,
    undefined,
    BACKEND_URL,
  );
}

// ─── Style documents (documents attached directly to the style) ──────────────

export interface StyleDocumentsListParams {
  page: number;
  count: number;
  name?: string;
  documentTypeId?: string;
  isArchived?: boolean;
  /** `field:DIR` — hook splits into `sort=<field>&order=<asc|desc>`. */
  sort?: string;
}

export interface StyleDocumentDto {
  id: string;
  name: string;
  fileId: string;
  fileUrl: string | null;
  documentTypeId: string;
  documentClass: string | null;
  documentType: {
    id: string;
    title: string;
    documentClass: 'TRANSACTIONAL' | 'COMPANY';
    isDeprecated: boolean;
    impactAreaId: string | null;
  };
  createdAt: string | null;
  updatedAt: string | null;
  isArchived: boolean;
  metadata: Record<string, unknown>;
}

export interface StyleDocumentsListResponse {
  data: StyleDocumentDto[];
  meta: {
    count: number;
    page: number;
    pageSize: number;
    totalCount: number;
  };
}

export function fetchStyleDocuments(
  styleId: string,
  qs: string,
): Promise<StyleDocumentsListResponse> {
  return apiFetch<StyleDocumentsListResponse>(
    `/styles/${styleId}/style-documents?${qs}`,
    undefined,
    BACKEND_URL,
  );
}

export interface CreateStyleDocumentBody {
  name: string;
  documentTypeId: string;
  documentClass: 'TRANSACTIONAL' | 'COMPANY';
  fileId: string;
}

export function createStyleDocument(
  styleId: string,
  body: CreateStyleDocumentBody,
): Promise<{ data: StyleDocumentDto }> {
  return apiFetch<{ data: StyleDocumentDto }>(
    `/styles/${styleId}/style-documents`,
    { method: 'POST', body: JSON.stringify(body) },
    BACKEND_URL,
  );
}

export function bulkArchiveStyleDocuments(
  styleId: string,
  ids: string[],
  isArchived: boolean,
): Promise<{ data: { documents: StyleDocumentDto[] } }> {
  return apiFetch<{ data: { documents: StyleDocumentDto[] } }>(
    `/styles/${styleId}/style-documents/bulk-archive`,
    { method: 'POST', body: JSON.stringify({ ids, isArchived }) },
    BACKEND_URL,
  );
}

export function bulkDeleteStyleDocuments(
  styleId: string,
  ids: string[],
): Promise<void> {
  return apiFetch<void>(
    `/styles/${styleId}/style-documents/bulk-delete`,
    { method: 'POST', body: JSON.stringify({ ids }) },
    BACKEND_URL,
  );
}

export function bulkDownloadStyleDocuments(
  styleId: string,
  fileIds: string[],
): Promise<{ data: { url: string } }> {
  return apiFetch<{ data: { url: string } }>(
    `/styles/${styleId}/style-documents/download`,
    { method: 'POST', body: JSON.stringify({ fileIds }) },
    BACKEND_URL,
  );
}

// ─── Style order documents (docs reaching this style via POs/SOs) ────────────

export interface StyleOrderDocumentsListParams {
  page: number;
  count: number;
  name?: string;
  documentTypeId?: string;
  orderNumber?: string;
  isArchived?: boolean;
  /** `field:DIR` — hook splits into `sort=<field>&order=<asc|desc>`. */
  sort?: string;
}

export interface StyleOrderDocumentDto {
  fileId: string;
  documentId: string | null;
  name: string;
  fileUrl: string | null;
  documentTypeId: string | null;
  documentType: {
    id: string | null;
    title: string;
    documentClass: 'TRANSACTIONAL' | 'COMPANY' | null;
    isDeprecated: boolean;
    impactAreaId: string | null;
  } | null;
  createdAt: string | null;
  updatedAt: string | null;
  isArchived: boolean;
  orderList: Array<{
    id: string;
    orderNumber: string;
    orderType: 'PURCHASE_ORDER' | 'SALES_ORDER';
    buyer: { id: string; name: string };
    supplier: { id: string; name: string };
  }>;
}

export interface StyleOrderDocumentsListResponse {
  data: StyleOrderDocumentDto[];
  meta: {
    count: number;
    page: number;
    pageSize: number;
    totalCount: number;
  };
}

export function fetchStyleOrderDocuments(
  styleId: string,
  qs: string,
): Promise<StyleOrderDocumentsListResponse> {
  return apiFetch<StyleOrderDocumentsListResponse>(
    `/styles/${styleId}/style-order-documents?${qs}`,
    undefined,
    BACKEND_URL,
  );
}

export function bulkDownloadStyleOrderDocuments(
  styleId: string,
  fileIds: string[],
): Promise<{ data: { url: string } }> {
  return apiFetch<{ data: { url: string } }>(
    `/styles/${styleId}/style-order-documents/download`,
    { method: 'POST', body: JSON.stringify({ fileIds }) },
    BACKEND_URL,
  );
}

export function fetchStylesList(
  params: StylesListParams,
): Promise<StyleListPage> {
  return apiFetch<StyleListPage>(
    '/styles/list',
    { method: 'POST', body: JSON.stringify(params) },
    BACKEND_URL,
  );
}

// Exact-match (case-insensitive) duplicate check by code value. Used by the
// bulk-importer to warn the user about codes that already exist for their
// company. Pass `isComponent: true` to scope the check to components only.
export async function checkStyleCodes(
  body: CheckStyleCodesBody,
): Promise<CheckStyleCodesItem[]> {
  const res = await apiFetch<{ data: CheckStyleCodesItem[] }>(
    '/styles/check-codes',
    { method: 'POST', body: JSON.stringify(body) },
    BACKEND_URL,
  );
  return res.data;
}

export function fetchVariantsList(
  params: VariantsListParams,
): Promise<VariantListPage> {
  return apiFetch<VariantListPage>(
    '/style-variants/list',
    { method: 'POST', body: JSON.stringify(params) },
    BACKEND_URL,
  );
}

export function createVariant(body: CreateVariantBody): Promise<unknown> {
  return apiFetch(
    '/style-variants',
    { method: 'POST', body: JSON.stringify(body) },
    BACKEND_URL,
  );
}

export function createSku(body: CreateSkuBody): Promise<unknown> {
  return apiFetch(
    '/variant-skus',
    { method: 'POST', body: JSON.stringify(body) },
    BACKEND_URL,
  );
}

export function archiveVariants(ids: string[]): Promise<unknown> {
  return apiFetch(
    '/style-variants/archive',
    { method: 'POST', body: JSON.stringify({ ids }) },
    BACKEND_URL,
  );
}

export function unarchiveVariants(payload: {
  ids: string[];
  shouldUnarchiveChildren: boolean;
}): Promise<unknown> {
  return apiFetch(
    '/style-variants/unarchive',
    { method: 'POST', body: JSON.stringify(payload) },
    BACKEND_URL,
  );
}

export function fetchSkusList(params: SkusListParams): Promise<SkuListPage> {
  return apiFetch<SkuListPage>(
    '/variant-skus/list',
    { method: 'POST', body: JSON.stringify(params) },
    BACKEND_URL,
  );
}

export function archiveSkus(ids: string[]): Promise<unknown> {
  return apiFetch(
    '/variant-skus/archive',
    { method: 'POST', body: JSON.stringify({ ids }) },
    BACKEND_URL,
  );
}

export function unarchiveSkus(ids: string[]): Promise<unknown> {
  return apiFetch(
    '/variant-skus/unarchive',
    { method: 'POST', body: JSON.stringify({ ids }) },
    BACKEND_URL,
  );
}

export async function fetchStyleDetail(id: string): Promise<StyleDetail> {
  const res = await apiFetch<{ data: StyleDetail }>(
    `/styles/${id}`,
    undefined,
    BACKEND_URL,
  );
  return res.data;
}

export async function fetchVariantDetail(id: string): Promise<VariantDetail> {
  const res = await apiFetch<{ data: VariantDetail }>(
    `/style-variants/${id}`,
    undefined,
    BACKEND_URL,
  );
  return res.data;
}

export async function fetchSkuDetail(id: string): Promise<SkuDetail> {
  const res = await apiFetch<{ data: SkuDetail }>(
    `/variant-skus/${id}`,
    undefined,
    BACKEND_URL,
  );
  return res.data;
}

// Dynamic per-style-type fields (e.g. Fabric width, Finish for DENIM_FABRIC,
// Yarn count for YARN). Driven server-side by the entity's `styleTypeId`,
// not by isComponent — applies equally to /my-styles and /my-components.
export async function fetchStyleTypeFieldValues(
  styleId: string,
): Promise<StyleTypeFieldValuesResponse> {
  const res = await apiFetch<{ data: StyleTypeFieldValuesResponse }>(
    `/style-type-field-values/entity?styleId=${encodeURIComponent(styleId)}`,
    undefined,
    BACKEND_URL,
  );
  return res.data;
}

export async function upsertStyleTypeFieldValues(
  body: UpsertStyleTypeFieldValuesBody,
): Promise<StyleTypeFieldValuesResponse> {
  const res = await apiFetch<{ data: StyleTypeFieldValuesResponse }>(
    '/style-type-field-values/upsert',
    { method: 'POST', body: JSON.stringify(body) },
    BACKEND_URL,
  );
  return res.data;
}

export async function updateStyle(
  id: string,
  body: UpdateStyleBody,
): Promise<StyleDetail> {
  const res = await apiFetch<{ data: StyleDetail }>(
    `/styles/${id}`,
    { method: 'PUT', body: JSON.stringify(body) },
    BACKEND_URL,
  );
  return res.data;
}

export async function updateVariant(
  id: string,
  body: UpdateVariantBody,
): Promise<VariantDetail> {
  const res = await apiFetch<{ data: VariantDetail }>(
    `/style-variants/${id}`,
    { method: 'PUT', body: JSON.stringify(body) },
    BACKEND_URL,
  );
  return res.data;
}

export async function updateSku(
  id: string,
  body: UpdateSkuBody,
): Promise<SkuDetail> {
  const res = await apiFetch<{ data: SkuDetail }>(
    `/variant-skus/${id}`,
    { method: 'PUT', body: JSON.stringify(body) },
    BACKEND_URL,
  );
  return res.data;
}

export function fetchStylePropertiesList(params: {
  category: 'brands' | 'departments' | 'seasons' | 'collections';
  companyId: string;
  page?: number;
  limit?: number;
}): Promise<StylePropertyPage> {
  return apiFetch<StylePropertyPage>(
    '/style-properties/list',
    { method: 'POST', body: JSON.stringify(params) },
    BACKEND_URL,
  );
}

export interface SupplyChainMapping {
  id: string;
  name: string;
}

export interface DppItem {
  styleId: string;
  tracingType:
    | 'SUPPLY_CHAIN_MAPPINGS'
    | 'CASCADE_MAPPINGS'
    | 'TRACING_PURCHASE_ORDERS'
    | 'TRACING_SALES_ORDERS';
  tracingId: string;
  isActive: boolean;
}

export function fetchSupplyChainMappings(): Promise<{
  data: SupplyChainMapping[];
}> {
  return apiFetch('/v3/supply-chains?isDropDown=true&isArchived=false');
}

export function createSupplyChain(name: string): Promise<{
  data: { id: string; name: string };
}> {
  return apiFetch('/v3/supply-chains', {
    method: 'POST',
    body: JSON.stringify({ name }),
  });
}

export function attachDppStory(items: DppItem[]): Promise<unknown> {
  return apiFetch(
    '/dpp',
    { method: 'POST', body: JSON.stringify({ items }) },
    BACKEND_URL,
  );
}

export interface BulkUpdateStylesResponse {
  metadata: { success: boolean };
  data: {
    updatedCount: number;
    errors?: Array<{ styleId: string; styleName: string; error: string }>;
  };
}

export function bulkUpdateStyles(payload: {
  styleIds: string[];
  isStoryActive?: boolean;
  isComponent?: boolean;
  collection?: Array<{ value: string }>;
}): Promise<BulkUpdateStylesResponse> {
  return apiFetch<BulkUpdateStylesResponse>(
    '/styles/bulk-update',
    { method: 'PUT', body: JSON.stringify(payload) },
    BACKEND_URL,
  );
}

export function exportStyles(styleIds: string[]): Promise<unknown> {
  return apiFetch(
    '/styles/export',
    { method: 'POST', body: JSON.stringify({ styleIds }) },
    BACKEND_URL,
  );
}

export function archiveStyles(ids: string[]): Promise<unknown> {
  return apiFetch(
    '/styles/archive',
    { method: 'POST', body: JSON.stringify({ ids }) },
    BACKEND_URL,
  );
}

export function unarchiveStyles(payload: {
  ids: string[];
  shouldUnarchiveChildren: boolean;
}): Promise<unknown> {
  return apiFetch(
    '/styles/unarchive',
    { method: 'POST', body: JSON.stringify(payload) },
    BACKEND_URL,
  );
}

export function createStyleLink(body: CreateStyleLinkBody): Promise<unknown> {
  return apiFetch(
    '/style-links/link',
    { method: 'POST', body: JSON.stringify(body) },
    BACKEND_URL,
  );
}

export function recalculateAllBadges(): Promise<unknown> {
  return apiFetch(
    '/recalculate-badges',
    { method: 'POST', body: JSON.stringify({}) },
    BACKEND_URL,
  );
}

export function assignDeepestOrders(ids: string[]): Promise<unknown> {
  return apiFetch(
    '/cascade-tracing/orders/calculations/product-stories',
    { method: 'POST', body: JSON.stringify({ ids }) },
    BACKEND_URL,
  );
}

export function createStyleProperty(body: {
  value: string;
  category: 'collections' | 'brands' | 'departments' | 'seasons';
}): Promise<{ data: { id: string; value: string } }> {
  return apiFetch(
    '/style-properties',
    { method: 'POST', body: JSON.stringify(body) },
    BACKEND_URL,
  );
}

export interface CreateStylePayload {
  name: string;
  styleTypeId: string;
  isComponent: boolean;
  codes: Array<{ codeId: string; codeValue: string }>;
  /** Creates the style on behalf of this company (e.g. buyer in tracing PO flow). */
  forCompanyId?: string;
  /** Optional style weight (unit + numeric value). Only sent when type has isWeightAvailable. */
  weight?: { unit: string; value: number };
  /**
   * Optional raw-material composition. Only sent when the style is created
   * inline (e.g. from the Link Style dialog) and the user supplies materials.
   * `weight` and `origin` are per-line optional.
   */
  material?: {
    lines: Array<{
      rawMaterialId: string;
      percentage: number;
      weight?: number;
      /** ISO 3166-1 alpha-2 country code */
      origin?: string;
    }>;
  };
}

export interface CreateStyleResponse {
  metadata: { success: boolean };
  data: { id: string; name: string } | null;
}

export function createStyle(
  payload: CreateStylePayload,
): Promise<CreateStyleResponse> {
  return apiFetch<CreateStyleResponse>(
    '/styles',
    { method: 'POST', body: JSON.stringify(payload) },
    BACKEND_URL,
  );
}

export interface StyleImportRow {
  styleCodeId: string;
  styleCodeIdType: string;
  styleName: string;
  styleTypeId: string;
  description: string;
  brands: string;
  departments: string;
  seasons: string;
  collections: string;
  vendorId: string;
  mainSupplierId: string;
  supplierId: string;
  rawMaterialId: string;
  rawMaterialPercentage: number | null;
  variantCode: string;
  variantColor: string;
  variantName: string;
  skuCode: string;
  size: string;
}

export function bulkImportStyles(payload: {
  styles: StyleImportRow[];
  isComponent?: boolean;
}): Promise<unknown> {
  return apiFetch(
    '/styles/bulk-import',
    { method: 'POST', body: JSON.stringify(payload) },
    BACKEND_URL,
  );
}

/** Kick off an async BOM bulk import from a previously-uploaded .xlsx file. */
export function bomBulkImport(
  fileId: string,
): Promise<{ data: { bulkImportId: string; message: string } }> {
  return apiFetch(
    '/bom-import/bulk-import',
    { method: 'POST', body: JSON.stringify({ fileId }) },
    BACKEND_URL,
  );
}

// ─── BOM ──────────────────────────────────────────────────────────────────────

/**
 * BOM endpoints respond with a flat envelope: `{ metadata, data }` for
 * collections, `{ data }` for single items. The shapes below match what the
 * Fastify route returns directly — do NOT layer an extra `data:` wrapper.
 */

interface SingleEnvelope<T> {
  data: T;
}

export function fetchBomHeaders(
  params: BomHeadersListParams,
): Promise<BomHeadersListResponse> {
  return apiFetch<BomHeadersListResponse>(
    '/bom-headers/list',
    { method: 'POST', body: JSON.stringify(params) },
    BACKEND_URL,
  );
}

export function createBomHeader(body: CreateBomHeaderBody): Promise<BomHeader> {
  return apiFetch<SingleEnvelope<BomHeader>>(
    '/bom-headers',
    { method: 'POST', body: JSON.stringify(body) },
    BACKEND_URL,
  ).then(r => r.data);
}

export function updateBomHeader(
  id: string,
  body: UpdateBomHeaderBody,
): Promise<BomHeader> {
  return apiFetch<SingleEnvelope<BomHeader>>(
    `/bom-headers/${id}`,
    { method: 'PUT', body: JSON.stringify(body) },
    BACKEND_URL,
  ).then(r => r.data);
}

export function deleteBomHeader(
  styleId: string,
  bomHeaderIds: string[],
): Promise<unknown> {
  return apiFetch(
    '/bom-headers/delete',
    {
      method: 'POST',
      body: JSON.stringify({ styleId, bomHeaderIds }),
    },
    BACKEND_URL,
  );
}

export function fetchBomLines(
  params: BomLinesListParams,
): Promise<BomLinesListResponse> {
  return apiFetch<BomLinesListResponse>(
    '/bom-lines/list',
    { method: 'POST', body: JSON.stringify(params) },
    BACKEND_URL,
  );
}

export function createBomLine(body: CreateBomLineBody): Promise<BomLine> {
  return apiFetch<SingleEnvelope<BomLine>>(
    '/bom-lines',
    { method: 'POST', body: JSON.stringify(body) },
    BACKEND_URL,
  ).then(r => r.data);
}

export function updateBomLine(
  id: string,
  body: UpdateBomLineBody,
): Promise<BomLine> {
  return apiFetch<SingleEnvelope<BomLine>>(
    `/bom-lines/${id}`,
    { method: 'PUT', body: JSON.stringify(body) },
    BACKEND_URL,
  ).then(r => r.data);
}

export function deleteBomLines(
  styleId: string,
  bomLineIds: string[],
): Promise<unknown> {
  return apiFetch(
    '/bom-lines/delete',
    {
      method: 'POST',
      body: JSON.stringify({ styleId, bomLineIds }),
    },
    BACKEND_URL,
  );
}

export function fetchBomPlacements(
  params: BomPlacementsListParams = {},
): Promise<BomPlacementsListResponse> {
  return apiFetch<BomPlacementsListResponse>(
    '/bom-placements/list',
    { method: 'POST', body: JSON.stringify(params) },
    BACKEND_URL,
  );
}

export function createBomPlacement(
  body: CreateBomPlacementBody,
): Promise<BomPlacement> {
  return apiFetch<SingleEnvelope<BomPlacement>>(
    '/bom-placements',
    { method: 'POST', body: JSON.stringify(body) },
    BACKEND_URL,
  ).then(r => r.data);
}

// ─── Materials ────────────────────────────────────────────────────────────────

/**
 * Material endpoints follow the same envelope shape as BOM endpoints:
 * `{ metadata, data }` for list responses, `{ data }` for single items.
 */

export function fetchMaterialHeaders(
  params: MaterialHeadersListParams,
): Promise<MaterialHeadersListResponse> {
  return apiFetch<MaterialHeadersListResponse>(
    '/material-headers/list',
    { method: 'POST', body: JSON.stringify(params) },
    BACKEND_URL,
  );
}

export function createMaterialHeader(
  body: CreateMaterialHeaderBody,
): Promise<MaterialHeader> {
  return apiFetch<SingleEnvelope<MaterialHeader>>(
    '/material-headers',
    { method: 'POST', body: JSON.stringify(body) },
    BACKEND_URL,
  ).then(r => r.data);
}

export function updateMaterialHeader(
  id: string,
  body: UpdateMaterialHeaderBody,
): Promise<MaterialHeader> {
  return apiFetch<SingleEnvelope<MaterialHeader>>(
    `/material-headers/${id}`,
    { method: 'PUT', body: JSON.stringify(body) },
    BACKEND_URL,
  ).then(r => r.data);
}

export function deleteMaterialHeader(
  styleId: string,
  materialHeaderIds: string[],
): Promise<unknown> {
  return apiFetch(
    '/material-headers/delete',
    {
      method: 'POST',
      body: JSON.stringify({ styleId, materialHeaderIds }),
    },
    BACKEND_URL,
  );
}

export function fetchMaterialLines(
  params: MaterialLinesListParams,
): Promise<MaterialLinesListResponse> {
  return apiFetch<MaterialLinesListResponse>(
    '/material-lines/list',
    { method: 'POST', body: JSON.stringify(params) },
    BACKEND_URL,
  );
}

export function createMaterialLine(
  body: CreateMaterialLineBody,
): Promise<MaterialLine> {
  return apiFetch<SingleEnvelope<MaterialLine>>(
    '/material-lines',
    { method: 'POST', body: JSON.stringify(body) },
    BACKEND_URL,
  ).then(r => r.data);
}

export function updateMaterialLine(
  id: string,
  body: UpdateMaterialLineBody,
): Promise<MaterialLine> {
  return apiFetch<SingleEnvelope<MaterialLine>>(
    `/material-lines/${id}`,
    { method: 'PUT', body: JSON.stringify(body) },
    BACKEND_URL,
  ).then(r => r.data);
}

export function deleteMaterialLines(
  styleId: string,
  materialLineIds: string[],
): Promise<unknown> {
  return apiFetch(
    '/material-lines/delete',
    {
      method: 'POST',
      body: JSON.stringify({ styleId, materialLineIds }),
    },
    BACKEND_URL,
  );
}

// ─── DPP Supply Chain ─────────────────────────────────────────────────────────

function qs(params: Record<string, string | number | boolean | undefined>) {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === '') continue;
    usp.set(k, String(v));
  }
  const out = usp.toString();
  return out ? `?${out}` : '';
}

export interface SupplyChainMappingsListParams {
  page: number;
  count: number;
  isArchived?: boolean;
  sort?: string;
  /** Stringified JSON, e.g. `{"name":"cotton"}` */
  filter?: string;
}

export function fetchSupplyChainMappingsPaginated(
  params: SupplyChainMappingsListParams,
): Promise<SupplyChainMappingsListResponse> {
  return apiFetch<SupplyChainMappingsListResponse>(
    `/v3/supply-chains${qs({ ...params })}`,
    {},
    API_BASE_URL,
  );
}

export interface CascadeMappingsListParams {
  page: number;
  count: number;
  isArchived?: boolean;
  /** Stringified JSON, e.g. `{"collaborationRights":"Creator","name":"..."}` */
  filter?: string;
}

export function fetchCascadeMappings(
  params: CascadeMappingsListParams,
): Promise<CascadeMappingsListResponse> {
  return apiFetch<CascadeMappingsListResponse>(
    `/v1/company-chains/collaborations${qs({ ...params })}`,
    {},
    CASCADE_MAP_SERVICE_URL,
  );
}

export function fetchStyleTracingOrders(
  styleId: string,
): Promise<StyleTracingOrdersResponse> {
  return apiFetch<StyleTracingOrdersResponse>(
    `/styles/${styleId}/traced-orders`,
    {},
    BACKEND_URL,
  );
}

export function fetchSupplyChainGraph(
  id: string,
  opts: { isRemovedStatusCheck?: boolean } = {},
): Promise<SupplyChainGraphResponse> {
  return apiFetch<SupplyChainGraphResponse>(
    `/v3/supply-chains/${id}${qs({ ...opts })}`,
    {},
    API_BASE_URL,
  );
}

export function fetchCascadeMapGraph(
  collaborationId: string,
): Promise<SupplyChainGraphResponse> {
  return apiFetch<SupplyChainGraphResponse>(
    `/v1/company-chains/collaborations/${collaborationId}/expand-all`,
    {},
    CASCADE_MAP_SERVICE_URL,
  );
}

export function fetchPurchaseOrderTracing(
  orderId: string,
): Promise<SupplyChainGraphResponse> {
  return apiFetch<SupplyChainGraphResponse>(
    `/v3/cascade-tracing/orders/trace/purchases/${orderId}`,
    {},
    API_BASE_URL,
  );
}

export function fetchSalesOrderTracing(
  orderId: string,
): Promise<SupplyChainGraphResponse> {
  return apiFetch<SupplyChainGraphResponse>(
    `/v3/cascade-tracing/orders/trace/sales/${orderId}`,
    {},
    API_BASE_URL,
  );
}

// ─── DPP Badges ───────────────────────────────────────────────────────────────

const KIND_TO_BADGES_SEGMENT: Record<EntityKindPath, string> = {
  styles: 'styles',
  variants: 'style-variants',
  skus: 'variant-skus',
};

/** GET `/{segment}/{id}/badges` — list product badges + proofs for an entity. */
export function fetchEntityBadges(
  kind: EntityKindPath,
  entityId: string,
): Promise<ProductBadgesResponse> {
  return apiFetch<ProductBadgesResponse>(
    `/${KIND_TO_BADGES_SEGMENT[kind]}/${entityId}/badges`,
    {},
    BACKEND_URL,
  );
}

/** POST `/recalculate-badges` — trigger backend re-evaluation. Legacy always
 *  posts `{ styleId }` regardless of entity kind; we match that. */
export function recalculateEntityBadges(entityId: string): Promise<unknown> {
  return apiFetch(
    '/recalculate-badges',
    { method: 'POST', body: JSON.stringify({ styleId: entityId }) },
    BACKEND_URL,
  );
}

/** GET `/v3/badges/:id` — full badge details including claimingRequirements
 *  (only fetched on demand by the claim modal). */
export function fetchBadgeById(badgeId: string): Promise<Badge> {
  return apiFetch<Badge>(`/v3/badges/${badgeId}`, {}, API_BASE_URL);
}

/** GET `/api/companies?isDropDown=true&includeOwnCompany=true`
 *  — companies dropdown for the DPP claim modal. Different from
 *  `fetchNetworkCompaniesDropdown` (which is supplier-network-only). */
export function fetchClaimCompaniesDropdown(): Promise<{
  data: CompanyDropdownOption[];
}> {
  return apiFetch<{ data: CompanyDropdownOption[] }>(
    '/api/companies?isDropDown=true&includeOwnCompany=true',
    {},
    API_ROOT_URL,
  );
}
